import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 *
 * Mobile:
 * - Visible before reaching the main buy buttons.
 * - Hidden while the main buy buttons are visible.
 * - Visible again after scrolling past them.
 * - Hidden close to the footer.
 *
 * Desktop:
 * - Keeps the original Fabric behaviour.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;


  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;


    /*
     * KENLESS:
     * escuchar cambios de los selectores
     * del sticky móvil.
     */
    this.addEventListener(
      'change',
      this.#handleStickyOptionChange,
      { signal }
    );


    const target =
      this.closest('.shopify-section');


    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );


    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
      { signal }
    );


    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartAddComplete,
      { signal }
    );


    document.addEventListener(
      ThemeEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );


    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );


    this.#getInitialQuantity();
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();

    this.#mainBottomObserver?.disconnect();

    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(
        this.#animationTimeout
      );
    }
  }


  /**
   * Sets up the IntersectionObserver
   * to watch the original buy buttons.
   */
  #setupIntersectionObserver() {
    const productForm =
      this.#getProductForm();

    if (!productForm) return;


    const buyButtonsBlock =
      productForm.closest(
        '.buy-buttons-block'
      );

    if (!buyButtonsBlock) return;


    /*
     * In themes migrated from 2.0,
     * footer may not exist.
     */
    const footer =
      document.querySelector('footer') ??
      document.querySelector(
        '[class*="footer-group"]'
      );

    if (!footer) return;


    /*
     * ==========================================
     * BUY BUTTONS OBSERVER
     * ==========================================
     */

    this.#buyButtonsIntersectionObserver =
      new IntersectionObserver(
        (entries) => {

          const [entry] =
            entries;

          if (!entry) return;


          /*
           * ======================================
           * MOBILE — SCUFFERS STYLE
           * ======================================
           *
           * Add to Cart original fuera:
           * sticky visible.
           *
           * Add to Cart original visible:
           * sticky oculto.
           *
           * Al pasarlo:
           * sticky vuelve a aparecer.
           */
          if (
            isMobileBreakpoint()
          ) {

            if (
              !entry.isIntersecting &&
              !this.#hiddenByBottom
            ) {

              this.#showStickyBar();

            } else if (
              entry.isIntersecting
            ) {

              this.#hiddenByBottom =
                false;

              this.#hideStickyBar();
            }


            return;
          }


          /*
           * ======================================
           * DESKTOP — FABRIC ORIGINAL
           * ======================================
           */

          if (
            !entry.isIntersecting &&
            !this.#isStuck
          ) {

            const rect =
              entry.target.getBoundingClientRect();


            /*
             * Solo mostrar después
             * de haber pasado los botones.
             */
            if (
              rect.bottom < 0 ||
              rect.top < 0
            ) {

              this.#showStickyBar();
            }

          } else if (
            entry.isIntersecting &&
            this.#isStuck
          ) {

            this.#hiddenByBottom =
              false;

            this.#hideStickyBar();
          }
        }
      );


    /*
     * ==========================================
     * FOOTER OBSERVER
     * ==========================================
     */

    this.#mainBottomObserver =
      new IntersectionObserver(
        (entries) => {

          const [entry] =
            entries;

          if (!entry) return;


          /*
           * Llegamos al footer:
           * ocultar sticky.
           */
          if (
            entry.isIntersecting &&
            this.#isStuck
          ) {

            this.#hiddenByBottom =
              true;

            this.#hideStickyBar();

            return;
          }


          /*
           * Salimos otra vez del footer.
           */
          if (
            !entry.isIntersecting &&
            this.#hiddenByBottom
          ) {

            const rect =
              buyButtonsBlock
                .getBoundingClientRect();


            /*
             * En móvil:
             * si el Add to Cart original
             * no está visible,
             * volvemos a mostrar.
             */
            if (
              isMobileBreakpoint()
            ) {

              const buyButtonsVisible =
                rect.bottom > 0 &&
                rect.top <
                  window.innerHeight;


              if (!buyButtonsVisible) {

                this.#hiddenByBottom =
                  false;

                this.#showStickyBar();
              }


              return;
            }


            /*
             * Desktop original.
             */
            if (
              rect.bottom < 0 ||
              rect.top < 0
            ) {

              this.#hiddenByBottom =
                false;

              this.#showStickyBar();
            }
          }
        },
        {
          rootMargin:
            '200px 0px 0px 0px',
        }
      );


    this.#buyButtonsIntersectionObserver
      .observe(
        buyButtonsBlock
      );


    this.#mainBottomObserver
      .observe(
        footer
      );


    this.#targetAddToCartButton =
      productForm.querySelector(
        '[ref="addToCartButton"]'
      );
  }


  /**
   * ==========================================
   * KENLESS — STICKY VARIANT SELECTOR
   * ==========================================
   *
   * Sincroniza el select flotante
   * con el VariantPicker original de Fabric.
   *
   * @param {Event} event
   */
  #handleStickyOptionChange = (event) => {

    const target =
      event.target;


    if (
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }


    if (
      !target.matches(
        '.sticky-add-to-cart__select'
      )
    ) {
      return;
    }


    const optionValueId =
      target.value;


    if (!optionValueId) {
      return;
    }


    const productId =
      this.dataset.productId;


    if (!productId) {
      return;
    }


    const section =
      this.closest(
        '.shopify-section'
      );


    if (!section) {
      return;
    }


    /*
     * Buscar el VariantPicker
     * original del mismo producto.
     */
    const variantPicker =
      section.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );


    if (!variantPicker) {
      return;
    }


    /*
     * Buscar exactamente el option value
     * seleccionado en nuestro sticky.
     */
    const originalOption =
      Array.from(
        variantPicker.querySelectorAll(
          '[data-option-value-id]'
        )
      ).find(
        (element) =>
          element.dataset.optionValueId ===
          optionValueId
      );


    if (!originalOption) {
      return;
    }


    /*
     * ==========================================
     * ORIGINAL PICKER = RADIO / BUTTON
     * ==========================================
     */

    if (
      originalOption instanceof
      HTMLInputElement
    ) {

      if (
        originalOption.disabled
      ) {
        return;
      }


      /*
       * click() hace que Fabric reciba
       * su change normal.
       */
      if (
        !originalOption.checked
      ) {

        originalOption.click();
      }


      return;
    }


    /*
     * ==========================================
     * ORIGINAL PICKER = SELECT
     * ==========================================
     */

    if (
      originalOption instanceof
      HTMLOptionElement
    ) {

      if (
        originalOption.disabled
      ) {
        return;
      }


      const originalSelect =
        originalOption.closest(
          'select'
        );


      if (!originalSelect) {
        return;
      }


      originalSelect.value =
        originalOption.value;


      originalSelect.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true,
          }
        )
      );
    }
  };


  // ==========================================
  // PUBLIC ACTION HANDLERS
  // ==========================================

  /**
   * Handles Add to Cart click
   * from the sticky bar.
   */
  handleAddToCartClick = async () => {

    if (
      !this.#targetAddToCartButton
    ) {
      return;
    }


    /*
     * Utilizamos el Add to Cart
     * REAL de la ficha de producto.
     */
    this.#targetAddToCartButton
      .dataset
      .puppet = 'true';


    this.#targetAddToCartButton
      .click();


    const cartIcon =
      document.querySelector(
        '.header-actions__cart-icon'
      );


    if (
      this.refs
        .addToCartButton
        .dataset
        .added !== 'true'
    ) {

      this.refs
        .addToCartButton
        .dataset
        .added = 'true';
    }


    /*
     * En móvil ocultamos la imagen
     * del sticky visualmente,
     * pero sigue existiendo en DOM.
     *
     * Por eso la animación de Fabric
     * puede seguir funcionando.
     */
    if (
      !cartIcon ||
      !this.refs.addToCartButton ||
      !this.refs.productImage
    ) {
      return;
    }


    if (
      this.#resetTimeout
    ) {

      clearTimeout(
        this.#resetTimeout
      );
    }


    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement(
          'fly-to-cart'
        )
      );


    flyToCartElement.classList.add(
      'fly-to-cart--sticky'
    );


    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );


    flyToCartElement.useSourceSize =
      'true';


    flyToCartElement.source =
      this.refs.productImage;


    flyToCartElement.destination =
      cartIcon;


    document.body.appendChild(
      flyToCartElement
    );


    await onAnimationEnd(
      [
        this.refs.addToCartButton,
        flyToCartElement,
      ]
    );


    this.#resetTimeout =
      setTimeout(
        () => {

          this.refs
            .addToCartButton
            .removeAttribute(
              'data-added'
            );

        },
        800
      );
  };


  /**
   * Handles variant update events.
   *
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {

    if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }


    const variant =
      event.detail.resource;


    /*
     * Get the freshly rendered
     * sticky bar from Shopify.
     */
    const newStickyAddToCart =
      event.detail.data.html
        .querySelector(
          'sticky-add-to-cart'
        );


    if (
      !newStickyAddToCart
    ) {
      return;
    }


    const newStickyBar =
      newStickyAddToCart
        .querySelector(
          '[ref="stickyBar"]'
        );


    if (!newStickyBar) {
      return;
    }


    /*
     * Preserve visibility while
     * changing size/color.
     */
    const currentStuck =
      this.refs.stickyBar
        .getAttribute(
          'data-stuck'
        ) ||
      'false';


    const variantAvailable =
      newStickyAddToCart
        .dataset
        .variantAvailable;


    /*
     * Actualiza:
     *
     * - selects
     * - price
     * - availability
     * - button
     * - image
     */
    morph(
      this.refs.stickyBar,
      newStickyBar,
      {
        childrenOnly: true,
      }
    );


    /*
     * Restore current sticky
     * visibility state.
     */
    this.refs.stickyBar
      .setAttribute(
        'data-stuck',
        currentStuck
      );


    this.dataset.variantAvailable =
      variantAvailable;


    /*
     * Actualizar variante actual.
     */
    if (
      variant &&
      variant.id
    ) {

      this.dataset.currentVariantId =
        variant.id;
    }


    /*
     * Re-cache original Add to Cart
     * after Fabric updates the product.
     */
    const productForm =
      this.#getProductForm();


    if (productForm) {

      this.#targetAddToCartButton =
        productForm.querySelector(
          '[ref="addToCartButton"]'
        );
    }


    if (
      variant == null
    ) {

      this.#handleVariantUnavailable();
    }


    /*
     * Restore current quantity.
     */
    this.#updateButtonText();
  };


  /**
   * Handles variant selected events.
   *
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {

    const variantId =
      event.detail.resource?.id;


    if (!variantId) {
      return;
    }


    this.dataset.currentVariantId =
      variantId;
  };


  /**
   * Updates variant title
   * when combination is unavailable.
   */
  #handleVariantUnavailable = () => {

    this.dataset.currentVariantId =
      '';


    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );


    const productId =
      this.dataset.productId;


    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );


    if (
      !variantTitleElement ||
      !variantPicker
    ) {
      return;
    }


    const selectedOptions =
      Array.from(
        variantPicker.querySelectorAll(
          'input:checked'
        )
      )
        .map(
          (option) =>
            /** @type {HTMLInputElement} */ (
              option
            ).value
        )
        .filter(
          (value) =>
            value !== ''
        )
        .join(' / ');


    if (
      !selectedOptions
    ) {
      return;
    }


    variantTitleElement.textContent =
      selectedOptions;
  };


  /**
   * Handles cart add complete.
   *
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {

    if (
      this.#targetAddToCartButton
    ) {

      this.#targetAddToCartButton
        .dataset
        .puppet = 'false';
    }
  };


  /**
   * Handles quantity selector updates.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {

    /*
     * Ignore cart drawer quantities.
     */
    if (
      event.detail.cartLine
    ) {
      return;
    }


    this.#currentQuantity =
      event.detail.quantity;


    this.#updateButtonText();
  };


  /**
   * Shows sticky bar.
   */
  #showStickyBar() {

    const { stickyBar } =
      this.refs;


    this.#isStuck =
      true;


    stickyBar.dataset.stuck =
      'true';
  }


  /**
   * Hides sticky bar.
   */
  #hideStickyBar() {

    const { stickyBar } =
      this.refs;


    this.#isStuck =
      false;


    stickyBar.dataset.stuck =
      'false';
  }


  // ==========================================
  // HELPER METHODS
  // ==========================================

  /**
   * Gets original product form.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {

    const productId =
      this.dataset.productId;


    if (!productId) {
      return null;
    }


    const sectionElement =
      this.closest(
        '.shopify-section'
      );


    if (!sectionElement) {
      return null;
    }


    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );


    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }


  /**
   * Gets initial quantity.
   */
  #getInitialQuantity() {

    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity ||
        '1'
      ) ||
      1;


    this.#updateButtonText();
  }


  /**
   * Updates quantity display.
   */
  #updateButtonText() {

    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;


    const available =
      !addToCartButton.disabled;


    quantityNumber.textContent =
      this.#currentQuantity
        .toString();


    if (
      available &&
      this.#currentQuantity > 1
    ) {

      quantityDisplay.style.display =
        'inline';

    } else {

      quantityDisplay.style.display =
        'none';
    }
  }
}


if (
  !customElements.get(
    'sticky-add-to-cart'
  )
) {

  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}